import type { StatisticStatus } from '../../common/types';
import type { ScoringConfigUpdate } from '../../common/api/scorecardThresholds';
import { orderedGradeEntries } from './validation';

export type ScoreCalculatorInput = {
  averageRating: number;
  reviewCount: number;
  responseRate: number;
  responseTimeHours: number | null;
  periodDays: number;
  customerVolume: number;
  businessType: string;
  source: string;
};

export type ScoreCalculatorMetric = {
  allocation: number;
  status: StatisticStatus;
};

export type ScoreCalculatorResult = {
  grade: string;
  score: number;
  critical: boolean;
  reviewVolume: ScoreCalculatorMetric;
  averageRating: ScoreCalculatorMetric;
  responseRate: ScoreCalculatorMetric;
  responseTime: ScoreCalculatorMetric;
};

function statusFor(
  allocation: number,
  status: { extremely_low: number; below: number; above: number },
): StatisticStatus {
  if (allocation < status.extremely_low) return 'extremely-low';
  if (allocation < status.below) return 'below-average';
  if (allocation >= status.above) return 'above-average';
  return 'average';
}

function descendingAllocation(
  value: number,
  thresholds: Array<{ threshold: number; allocation: number }>,
): number {
  return (thresholds.find((item) => value >= item.threshold)?.allocation ?? 0) / 100;
}

export function calculateDraftScore(
  input: ScoreCalculatorInput,
  config: ScoringConfigUpdate,
): ScoreCalculatorResult {
  if (input.periodDays <= 0 || input.customerVolume <= 0) {
    throw new Error('Period and monthly customers must be positive');
  }
  const review = config.review_volume;
  const effectiveSource = review.source_map[input.source] ?? review.unknown_source_default;
  const effectiveBusinessType = Object.prototype.hasOwnProperty.call(
    review.customer_volume_defaults,
    input.businessType,
  )
    ? input.businessType
    : review.unknown_business_type_default;
  const reviewPercent =
    (((input.reviewCount / input.periodDays) * 30) / input.customerVolume) * 100;
  const reviewAllocation = descendingAllocation(
    reviewPercent,
    review.thresholds[effectiveSource][effectiveBusinessType],
  );
  const ratingAllocation = descendingAllocation(
    input.averageRating,
    config.average_rating.thresholds,
  );
  const responseRateAllocation = descendingAllocation(
    input.responseRate,
    config.response_rate.thresholds,
  );
  const responseTimeAllocation =
    input.responseTimeHours === null
      ? 0
      : (config.response_time.thresholds.find(
          (item) => input.responseTimeHours !== null && input.responseTimeHours <= item.threshold,
        )?.allocation ?? 0) / 100;
  const critical =
    input.averageRating <= config.average_rating.critical_threshold ||
    reviewAllocation <= review.critical_thresholds[effectiveSource][effectiveBusinessType] ||
    responseRateAllocation <= config.response_rate.critical_threshold ||
    responseTimeAllocation <= config.response_time.critical_threshold;
  const score =
    config.weights.review_count * reviewAllocation +
    config.weights.average_rating * ratingAllocation +
    config.weights.response_rate * responseRateAllocation +
    config.weights.response_time * responseTimeAllocation;
  const grade = critical
    ? 'F'
    : (orderedGradeEntries(config.grade_thresholds).find(
        ([, threshold]) => score >= threshold,
      )?.[0] ?? 'F');

  return {
    grade,
    score,
    critical,
    reviewVolume: {
      allocation: reviewAllocation,
      status: statusFor(reviewAllocation, review.statuses[effectiveSource][effectiveBusinessType]),
    },
    averageRating: {
      allocation: ratingAllocation,
      status: statusFor(ratingAllocation, config.average_rating.status),
    },
    responseRate: {
      allocation: responseRateAllocation,
      status: statusFor(responseRateAllocation, config.response_rate.status),
    },
    responseTime: {
      allocation: responseTimeAllocation,
      status: statusFor(responseTimeAllocation, config.response_time.status),
    },
  };
}
